/* eslint-disable camelcase */
import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Link} from '../../../../../../components/common/links/link';
import {isIntegrationSettingFieldLoading} from '../../../../../../helpers/integrationHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  HipchatIntegrationModel,
  HipchatIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {decryptIntegrationSettingsAsyncAction} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Constants.
 */

const defaultConfig: HipchatIntegrationSettingsModel = {
  default_room_id: '',
  auth_token: '',
};

/*
 * Props.
 */

interface SettingsAppsConfigureHipchatProps {
  integration?: HipchatIntegrationModel;
  configurationSettings?: HipchatIntegrationSettingsModel;
  onIsSaveEnabled: HandlerOf<boolean>;
  setConfigurationSettings: HandlerOf<HipchatIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledDescriptionWrapperDiv = styled.div`
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureHipchat: FC<SettingsAppsConfigureHipchatProps> = (props) => {
  const {
    configurationSettings = defaultConfig,
    integration,
    onIsSaveEnabled,
    setConfigurationSettings,
  } = props;
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const dispatch = useAppDispatch();

  const [isDecrypting, setIsDecrypting] = useState(computeIsDecrypting(isGlobalAdmin, configurationSettings));

  // When we first load the settings, we may need to decrypt the settings.
  useInitialEffect(() => {
    if (!integration) {
      return;
    }
    dispatch(decryptIntegrationSettingsAsyncAction(integration));
  });

  // Controls if save is enabled for this dialog.
  //  This requires the api-key to be filled out, but there is no validation.
  useEffect(() => {
    if (!configurationSettings) {
      onIsSaveEnabled(false);
      return;
    }
    const {auth_token, default_room_id} = configurationSettings;
    const authTokenValid = Boolean(
      auth_token && auth_token.trim() !== '' && !isIntegrationSettingFieldLoading(auth_token),
    );
    const roomIdValid = Boolean(
      default_room_id && default_room_id.trim() !== '' && !isIntegrationSettingFieldLoading(default_room_id),
    );
    onIsSaveEnabled(authTokenValid && roomIdValid);
  }, [configurationSettings, onIsSaveEnabled]);

  // Controls if we are still decrypting or not.
  useEffect(() => {
    const isFieldLoading =
      isIntegrationSettingFieldLoading(configurationSettings.auth_token || '') ||
      isIntegrationSettingFieldLoading(configurationSettings.default_room_id || '');
    if (isDecrypting && !isFieldLoading) {
      setIsDecrypting(false);
    }
  }, [
    isDecrypting,
    setConfigurationSettings,
    configurationSettings.auth_token,
    configurationSettings.default_room_id,
  ]);

  return (
    <>
      {isGlobalAdmin && (
        <StyledDescriptionWrapperDiv>
          <Trans id="settings-apps-configure-app-dialog-hipchat-description">
            This integration adds new actions to the
          </Trans>{' '}
          <Link to="/settings/global/rules">
            <Trans id="settings-apps-configure-app-dialog-hipchat-description-rules-link">rules</Trans>
          </Link>
        </StyledDescriptionWrapperDiv>
      )}
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.auth_token || '',
        <Trans id="settings-apps-configure-app-dialog-hipchat-auth-token">Auth token</Trans>,
        (value) => {
          setConfigurationSettings({
            ...configurationSettings,
            auth_token: value,
          });
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting,
          isRequired: true,
        },
      )}
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.default_room_id || '',
        <Trans id="settings-apps-configure-app-dialog-hipchat-room-id">Room id</Trans>,
        (value) => {
          setConfigurationSettings({
            ...configurationSettings,
            default_room_id: value,
          });
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting,
          isRequired: true,
        },
      )}
    </>
  );
};

/*
 * Helpers.
 */

function computeIsDecrypting(
  isGlobalAdmin: boolean,
  configurationSettings?: HipchatIntegrationSettingsModel,
) {
  if (!isGlobalAdmin) {
    return false;
  }
  if (!configurationSettings || !configurationSettings.auth_token) {
    return false;
  }
  return isIntegrationSettingFieldLoading(configurationSettings.auth_token);
}
